import React, { Component } from 'react';
import { toast } from 'react-toastify';
import PropTypes from 'prop-types';
import {
    EmvioLoadingSpinner as LoadingSpinner
} from '@nexio/emvio-util-ui';
import {
    EmvioValidatedForm as Form,
    EmvioValidatedButton as Button,
    EmvioValidatedInput as Input,
    EmvioSelect as Select
} from '@nexio/emvio-form-components';
import { selfShape } from '../../utils/propTypes';
import { updateTimezone, updateUser } from './userSettingsActions';
import {
    getIsLoading as getIsUserLoading,
    getSelf
} from '../Self/selfEntity';
import {
    getIsGetTimezoneLoading,
    getIsUpdateTimezoneLoading,
    getIsUpdateTimezoneSuccessful,
    getIsUpdateUserLoading,
    getIsUpdateUserSuccessful,
    getTimezone,
    getUpdateTimezoneErrorMessage,
    getUpdateUserErrorMessage
} from './userSettingsEntity';
import { withRouter } from 'react-router';
import { connect } from 'react-redux';
import { doesPropExistAndHasItChanged } from '@nexio/emvio-util-app';
import {
    toastIfError
} from '../../utils/toast';
import './UserSettings.scss';
import { timezones } from '../../utils/timezones';

const TIMEZONE_DROPDOWN_MAX_HEIGHT = 300;
const TIMEZONE_DROPDOWN_VIEWPORT_PADDING = 8;

export class UserSettings extends Component {
    static propTypes = {
        error: PropTypes.string,
        isUserLoading: PropTypes.bool,
        isUpdateUserLoading: PropTypes.bool,
        isUpdateUserSuccessful: PropTypes.bool,
        onUpdateUser: PropTypes.func,
        user: selfShape,
        updateTimezoneError: PropTypes.string,
        isUpdateTimezoneLoading: PropTypes.bool,
        isUpdateTimezoneSuccessful: PropTypes.bool,
        isGetTimezoneLoading: PropTypes.bool,
        timezone: PropTypes.string,
        onUpdateTimezone: PropTypes.func
    };

    static defaultProps = {
        user: {},
        timezone: ''
    };

    constructor(props) {
        super(props);
        this.updateFirstName = this.updateFirstName.bind(this);
        this.updateLastName = this.updateLastName.bind(this);
        this.updatePhone = this.updatePhone.bind(this);
        this.submitForm = this.submitForm.bind(this);
        this.getInputFieldsFromUser = this.getInputFieldsFromUser.bind(this);
        this.onSelectTimezone = this.onSelectTimezone.bind(this);
        this.getDefaultTimezone = this.getDefaultTimezone.bind(this);
        this.positionTimezoneDropdown = this.positionTimezoneDropdown.bind(this);
        this.timezoneSelectRef = React.createRef();

        this.state = {
            ...this.getInputFieldsFromUser(),
            timezone: props.timezone
        };
    }

    componentDidMount() {
        document.addEventListener('scroll', this.positionTimezoneDropdown, true);
        window.addEventListener('resize', this.positionTimezoneDropdown);
    }

    componentWillUnmount() {
        document.removeEventListener('scroll', this.positionTimezoneDropdown, true);
        window.removeEventListener('resize', this.positionTimezoneDropdown);
    }

    componentDidUpdate(previousProps) {
        toastIfError(previousProps, this.props);
        toastIfError(previousProps, this.props, 'updateTimezoneError');

        if (doesPropExistAndHasItChanged(previousProps, this.props, 'user')) {
            this.setState({
                ...this.getInputFieldsFromUser()
            });
        }

        if (doesPropExistAndHasItChanged(previousProps, this.props, 'isUpdateUserSuccessful') && this.props.isUpdateUserSuccessful === true) {
            toast('User updated successfully!', { type: 'success' });
        }

        if (doesPropExistAndHasItChanged(previousProps, this.props, 'isUpdateTimezoneSuccessful') && this.props.isUpdateTimezoneSuccessful === true) {
            toast('Timezone updated successfully!', { type: 'success' });
        }
    }

    getInputFieldsFromUser() {
        const user = this.props.user;

        return {
            firstName: user.firstName ? user.firstName : '',
            lastName: user.lastName ? user.lastName : '',
            email: user.userName ? user.userName : '',
            phone: user.phone ? user.phone : ''
        };
    }

    submitForm() {
        const { firstName, lastName, phone } = this.state;
        const userUpdate = {
            ...this.props.user,
            firstName,
            lastName,
            phone: phone ? phone : null
        };
        this.props.onUpdateUser(userUpdate);
        this.props.onUpdateTimezone(this.state.timezone);
    }

    updateFirstName(event) {
        const firstName = event.target.value;
        this.setState({
            firstName
        });
    }

    updateLastName(event) {
        const lastName = event.target.value;
        this.setState({
            lastName
        });
    }

    updatePhone(event) {
        const phone = event.target.value;
        this.setState({
            phone
        });
    }

    getDefaultTimezone() {
        if (!this.props.timezone) {
            return { value: 'UTC', label: 'UTC' };
        } else {
            return timezones.find((timezoneObject) => timezoneObject.value === this.props.timezone);
        }
    }

    onSelectTimezone(timezone) {
        this.setState({ timezone });
    }

    positionTimezoneDropdown() {
        requestAnimationFrame(() => {
            const root = this.timezoneSelectRef.current;

            if (!root) {
                return;
            }

            const options = root.querySelector('.options');
            const selector = root.querySelector('.selector');

            if (!options || !selector) {
                return;
            }

            if (!options.classList.contains('open')) {
                options.style.position = '';
                options.style.top = '';
                options.style.left = '';
                options.style.width = '';
                options.style.zIndex = '';
                options.style.maxHeight = '';
                options.style.overflowY = '';

                return;
            }

            const { bottom, left, width } = selector.getBoundingClientRect();
            const availableBelow = window.innerHeight - bottom - TIMEZONE_DROPDOWN_VIEWPORT_PADDING;
            const maxHeight = Math.min(
                TIMEZONE_DROPDOWN_MAX_HEIGHT,
                Math.max(0, availableBelow)
            );

            options.style.position = 'fixed';
            options.style.top = `${bottom}px`;
            options.style.left = `${left}px`;
            options.style.width = `${width}px`;
            options.style.zIndex = '9999';
            options.style.maxHeight = `${maxHeight}px`;
            options.style.overflowY = 'auto';
        });
    }

    render() {
        return (
            <div className="emvio-user-settings">
                {this.props.isUserLoading || this.props.isGetTimezoneLoading
                    ? <LoadingSpinner />
                    : <Form onSubmit={this.submitForm}>
                        <Input
                            invert={true}
                            disabled
                            labelText="Email"
                            name="email"
                            validations={['required']}
                            value={this.state.email}
                            onChange={() => {}}
                            type="text"
                        />
                        <Input
                            invert={true}
                            labelText="First Name"
                            name="firstName"
                            validations={['required']}
                            value={this.state.firstName}
                            onChange={this.updateFirstName}
                            type="text"
                        />
                        <Input
                            invert={true}
                            labelText="Last Name"
                            name="lastName"
                            validations={['required']}
                            value={this.state.lastName}
                            onChange={this.updateLastName}
                            type="text"
                        />
                        <Input
                            invert={true}
                            labelText="Phone"
                            name="phone"
                            onChange={this.updatePhone}
                            shouldEnforcePhone
                            type="text"
                            validations={['phoneNumber']}
                            value={this.state.phone}
                        />
                        <div
                            ref={this.timezoneSelectRef}
                            onClick={this.positionTimezoneDropdown}
                        >
                            <Select
                                className="timezone-selector" label="Timezone"
                                options={timezones}
                                onChange={this.onSelectTimezone}
                                defaultSelectedOption={this.getDefaultTimezone()}
                            />
                        </div>
                        <Button
                            className="emvio-user-settings-button"
                            invertColors={true}
                            isLoading={this.props.isUpdateUserLoading}>
                            Save
                        </Button>
                    </Form>
                }
            </div>
        );
    }
}

function mapStateToProps(state) {
    return {
        error: getUpdateUserErrorMessage(state),
        isUserLoading: getIsUserLoading(state),
        isUpdateUserLoading: getIsUpdateUserLoading(state),
        isUpdateUserSuccessful: getIsUpdateUserSuccessful(state),
        user: getSelf(state),
        updateTimezoneError: getUpdateTimezoneErrorMessage(state),
        isUpdateTimezoneLoading: getIsUpdateTimezoneLoading(state),
        isUpdateTimezoneSuccessful: getIsUpdateTimezoneSuccessful(state),
        isGetTimezoneLoading: getIsGetTimezoneLoading(state),
        timezone: getTimezone(state)
    };
}

function mapDispatchToProps(dispatch) {
    return {
        onUpdateUser: (user) => dispatch(updateUser(user)),
        onUpdateTimezone: (timezone) => dispatch(updateTimezone(timezone))
    };
}

export default withRouter(
    connect(mapStateToProps, mapDispatchToProps)(UserSettings)
);
